import "maplibre-gl/dist/maplibre-gl.css";
import "./style.css";
import { getIdentity, magicLogin, exitToAccount, type Identity } from "./api";
import { GameClient } from "./net";
import { mountAuth, mountResetPassword, mountDashboard } from "./ui/account";
import { mountAdmin } from "./ui/admin";
import { mountGm } from "./ui/gm";
import { mountPodGm } from "./ui/podGm";
import { mountSeeker } from "./ui/seeker";
import { mountHider } from "./ui/hider";
import { mountCalculator } from "./ui/calculator";

const root = document.getElementById("app-root")!;

function route() {
  const id = getIdentity();
  if (!id) return mountAuth(root, () => route());
  // Account in a game (game-scoped session) → game view; ← returns to dashboard.
  if (id.acct && id.gameId) return openGame(id, id.name, () => { exitToAccount(); location.reload(); });
  if (id.acct) return mountDashboard(root); // account dashboard
  if (id.gm) return mountAdmin(root, (gameId, name) => openGame({ ...id, gameId }, name, () => route())); // legacy GM
  if (!id.gameId) return mountAuth(root, () => route());
  openGame(id, "", null); // legacy magic-link player
}

// Connect first, then route by the LIVE role from the server (which can change
// when a team claims hider). A role change resets every screen via reload.
function openGame(id: Identity, title: string, onExit: (() => void) | null) {
  const client = new GameClient(id.gameId!);
  let mounted = false;
  let currentRole: string | null = null;

  client.onReinit = () => {
    const role = id.gm ? "gm" : client.role ?? "seeker";
    if (!mounted) {
      mounted = true;
      currentRole = role;
      if (role === "hider") mountHider(root, client, { onExit: onExit ?? undefined });
      else if (id.gm) (client.mode === "podrotation" ? mountPodGm : mountGm)(root, client, { title: title || id.name, onExit: onExit ?? undefined });
      else mountSeeker(root, client, { title: title || id.name, onExit: onExit ?? undefined });
    } else if (role !== currentRole) {
      location.reload(); // hider↔seeker swap — clean full reset for everyone
    }
  };

  client.connect();
  // Silently record this seeker's path for the recap video (no-op for GM/hiders).
  if (!id.gm) import("./track").then(({ mountSilentTracking }) => mountSilentTracking(client));
  (window as unknown as { client: GameClient }).client = client;
}

// A magic-login link (?login=token) signs the player in, then we strip it.
async function boot() {
  const params = new URLSearchParams(location.search);
  // Recap GIF demo (?recap=1) — iterate on the round-recap animation standalone.
  if (params.get("recap")) { const { mountRecapDemo } = await import("./recapDemo"); return mountRecapDemo(root); }
  // Calculator mode (?calc=1) — standalone seeker toolkit, no login required.
  if (params.get("calc")) return mountCalculator(root);
  // Password-reset link (?reset=token) → show the reset form before anything else.
  const reset = params.get("reset");
  if (reset) {
    params.delete("reset");
    history.replaceState({}, "", location.pathname + (params.toString() ? `?${params}` : ""));
    return mountResetPassword(root, reset, () => route());
  }
  // Invite link (?invite=token) → stash it; the dashboard claims it once the
  // user is signed in (after logging in or creating an account).
  const invite = params.get("invite");
  if (invite) {
    sessionStorage.setItem("jl_invite", invite);
    params.delete("invite");
    history.replaceState({}, "", location.pathname + (params.toString() ? `?${params}` : ""));
  }
  const token = params.get("login");
  if (token) {
    try { await magicLogin(token); } catch (e) { alert((e as Error).message); }
    params.delete("login");
    history.replaceState({}, "", location.pathname + (params.toString() ? `?${params}` : ""));
  }
  route();
}
boot();
